import type { ApiConfig, AppEnvironment } from '@blufin/types';

/**
 * Minimal base interface for environment clients.
 * Platform-specific interfaces extend this with additional fields.
 */
export interface IEnvironmentClientAlpha<TCustom extends object = {}> {
  /** HTTP client configuration for service endpoints */
  readonly apiConfig: ApiConfig;
  /** Custom app configuration fields (excludes base fields) */
  readonly appConfig: TCustom;
  /** Deployment environment */
  readonly environment: string;
  /** Get all environment variables as a single object */
  getAllEnvironmentVariables(): Record<string, any>;
  /** App version from build (calendar format: YYYY.WW.PATCH) */
  readonly version: string;
}

/**
 * Configuration object passed to the abstract base class constructor.
 * Subclasses compute these values and pass them to super().
 */
export interface EnvironmentConfig<TCustom extends object = {}> {
  /** HTTP client configuration for service endpoints */
  apiConfig: ApiConfig;
  /** Custom app configuration fields (excludes base fields) */
  appConfig: TCustom;
  /** Deployment environment */
  environment: AppEnvironment;
  /** App version from build (calendar format: YYYY.WW.PATCH) */
  version: string;
}

/**
 * Abstract base class for environment clients.
 * Provides shared functionality for reading and validating environment configuration.
 * Subclasses must implement initialization logic specific to their platform (React, Electron, etc).
 */
export abstract class AbstractEnvironmentClientAlpha<TCustom extends object = {}>
  implements IEnvironmentClientAlpha<TCustom>
{
  public readonly apiConfig: ApiConfig;
  public readonly appConfig: TCustom;
  public readonly environment: AppEnvironment;
  public readonly version: string;

  /**
   * Protected constructor for subclasses.
   * Initializes all environment configuration properties with proper typing.
   * @param config - Configuration object containing version, environment, apiConfig, and customConfig.
   */
  protected constructor(config: EnvironmentConfig<TCustom>) {
    this.apiConfig = config.apiConfig;
    this.appConfig = config.appConfig;
    this.environment = config.environment;
    this.version = config.version;
  }

  /**
   * Abstract method for getting all environment variables.
   * Subclasses implement this with their specific return type.
   */
  public abstract getAllEnvironmentVariables(): Record<string, any>;

  /**
   * Validates that an environment value is in the list of valid values.
   * Throws an error if validation fails.
   * @param value - The environment value to validate.
   * @param validValues - The list of valid values.
   * @param fieldName - The name of the field being validated (used in error messages).
   */
  protected validateEnvironmentValue(value: string, validValues: string[], fieldName: string = 'environment'): void {
    if (!validValues.includes(value))
      throw new Error(`Invalid ${fieldName} "${value}". Must be one of: ${validValues.join(', ')}`);
  }

  /**
   * Validates that a required field is present.
   * Throws an error if the field is falsy.
   * @param value - The value to validate.
   * @param fieldName - The name of the field being validated (used in error messages).
   */
  protected validateRequiredField(value: any, fieldName: string): void {
    if (!value) throw new Error(`Runtime config missing required field: ${fieldName}`);
  }
}
